import React, { useEffect, useCallback, useState } from "react";
import { DocumentNode, useQuery } from "@apollo/client";
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";
import { ReactSVG } from "react-svg";

// molecules
import {
  PageHeader,
  BaseCard,
  BaseCardHeader,
  BaseCardAction,
  BaseCardItem
} from "@molecules/index";

// icons
import {
  ActiveUsersStatus,
  InactiveUsersStatus,
  AllUsersStatus,
  BlockedUsersStatus,
  NewUsersStatus
} from "src/assets/icons";

// store
import Store from "@store/jotai";

// atoms
import { Loading } from "@atoms/index";
import AlertContentByClient from "@atoms/AlertContentByClient";
import ShowMoreButton from "@atoms/ShowMoreButton";

// graphql
import {
  UserQueriesGetAllWithPaginationArgs,
  UserType,
  ECommandResultStatus
} from "@store/graphql/helpers/types.graphql";
import {
  userAllWithPagination,
  TUserAllWithPaginationQueries
} from "@graphql/groups/user/query.graphql";

// Libraries
import { Grid } from "@libraries/mui";

// Language
import { AlertMessages, CustomerMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";

import * as Styled from "./styles";
import Office from "../Office";

const Permission: React.FC = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const {
    value: users,
    setValue: setUsers,
    filteredUsers,
    setFilteredUsers
  } = Store.useUsers();

  const filterOptions = {
    newUsers: "newUsers",
    allUsers: "allUsers",
    activeUsers: "activeUsers",
    inactiveUsers: "inactiveUsers",
    blocked: "blocked"
  };

  const [nextPage, setNextPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [selectedFilter, setSelectedFilter] = useState(filterOptions.allUsers);
  const [idsUnfilteredUsers, setIdsUnfilteredUsers] = useState(Array<string>());
  const [idsNewUsers, setIdsNewUsers] = useState(Array<string>());

  interface Filter {
    id: string;
    title: string;
    icon: string;
  }

  const filters: Filter[] = [
    {
      id: filterOptions.allUsers,
      title: t(CustomerMessages.allUsersStatusIconTitle),
      icon: AllUsersStatus
    },
    {
      id: filterOptions.newUsers,
      title: t(CustomerMessages.newUsersStatusIconTitle),
      icon: NewUsersStatus
    },
    {
      id: filterOptions.activeUsers,
      title: t(CustomerMessages.activeUsersStatusIconTitle),
      icon: ActiveUsersStatus
    },
    {
      id: filterOptions.inactiveUsers,
      title: t(CustomerMessages.inactiveUsersStatusIconTitle),
      icon: InactiveUsersStatus
    },
    {
      id: filterOptions.blocked,
      title: t(CustomerMessages.blockedUsersStatusIconTitle),
      icon: BlockedUsersStatus
    }
  ];

  const { loading, error, data } = useQuery<
    TUserAllWithPaginationQueries,
    UserQueriesGetAllWithPaginationArgs
  >(
    userAllWithPagination([
      "id",
      "fullName",
      "role",
      "clientName",
      "email",
      "isValidated",
      "phoneNumber",
      "document",
      "createdAt",
      "active",
      "permissions.id"
    ]) as DocumentNode,
    {
      fetchPolicy: "network-only",
      variables: {
        page: nextPage,
        perPage: 500
      }
    }
  );

  const loadUsers = useCallback(async () => {
    const response = data?.user.getAllWithPagination;

    if (response?.status === ECommandResultStatus.SUCCESS) {
      setTotalPages(response.pageCount);

      let usersResult = response.result as UserType[];

      if (nextPage > 1) usersResult = [...users, ...usersResult];

      const usersSorted = usersResult?.sort((a, b) => {
        if (!a || !b) return 0;

        return a.fullName.localeCompare(b.fullName);
      });

      const active = usersSorted?.filter((user) => user?.active) as UserType[];
      const inactive = usersSorted?.filter((user) => !user?.active) as UserType[];

      setUsers([...active, ...inactive]);
    }
  }, [data, setUsers]);

  const filterUsers = useCallback((users: UserType[]) => {
    const filteredUsers: UserType[] = [];
    const unfilteredUsers: UserType[] = [];
    const idsNewUsers: string[] = [];
    const idsUnfilteredUsers: string[] = [];

    // get new users
    users.forEach((user) => {
      const sevenDays = 7;
      const differenceInDays = Math.abs(new Date(user.createdAt).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24);

      if (!user.permissions?.length && user.active && differenceInDays < sevenDays) idsNewUsers.push(user.id);

      // define if the user will enter the filter
      let filteredUser = false;

      switch (selectedFilter) {
        case filterOptions.newUsers:
          filteredUser = idsNewUsers.includes(user.id);
          break;
        case filterOptions.activeUsers:
          filteredUser = user?.isValidated && user?.active;
          break;
        case filterOptions.inactiveUsers:
          filteredUser = !user?.active;
          break;
        case filterOptions.blocked:
          filteredUser = !user?.isValidated && user?.active;
          break;
        default:
          filteredUser = true;
          break;
      }

      if (filteredUser) {
        filteredUsers.push(user);
      } else {
        unfilteredUsers.push(user);
        idsUnfilteredUsers.push(user.id);
      }
    });

    setIdsUnfilteredUsers(idsUnfilteredUsers);
    setIdsNewUsers(idsNewUsers);

    return [...filteredUsers, ...unfilteredUsers];
  }, [selectedFilter, users]);

  const handleNextPage = () => setNextPage(nextPage + 1);

  useEffect(() => {
    if (data) loadUsers();
    if (error) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: error.message
      });
    }
  }, [loadUsers, data, error, addToast]);

  useEffect(() => {
    setFilteredUsers(filterUsers(users));
  }, [selectedFilter, users, setFilteredUsers, filterUsers]);

  useEffect(() => () => {
    setUsers([]);
    setFilteredUsers([]);
    setNextPage(1);
    setTotalPages(0);
  }, [setUsers, setFilteredUsers]);

  const detailsUser = useCallback((user: UserType) => (
    <BaseCardItem
      key={user.id}
      type="user"
      user={user}
    />
  ), []);

  return (
    <Office showSelectClient={false}>
      <Styled.HeaderContainer>
        <PageHeader url="/" title={t(CustomerMessages.applicationPermission)} />
        <Styled.IconsContainer>
          {
            filters.map((filter) => (
              <Styled.FilterButton
                key={filter.id}
                title={filter.title}
                onClick={() => setSelectedFilter(filter.id)}
              >
                <ReactSVG src={filter.icon} />
                <Styled.Identifier active={selectedFilter === filter.id} />
              </Styled.FilterButton>
            ))
          }
        </Styled.IconsContainer>
      </Styled.HeaderContainer>
      <Styled.Container>
        <Grid container spacing={2}>
          {loading && (
            <Grid item xs={12}>
              <Loading loading />
            </Grid>
          )}
          {!loading
            && filteredUsers?.length === 0 && (
              <Grid item xs={12}>
                <AlertContentByClient alertMessage={t(AlertMessages.noUser)} iconColor="yellow" />
              </Grid>
          )}
          {!loading
            && filteredUsers
            && filteredUsers.map((user) => (
              <Grid key={`grid-${user.id}`} item xs={12} sm={6} md={4}>
                <BaseCard
                  key={user.id}
                  id={user.id}
                  header={<BaseCardHeader type="user" data={user} />}
                  actions={(
                    <BaseCardAction
                      type="permissionUser"
                      user={user}
                      inactive={idsUnfilteredUsers.includes(user.id)}
                    />
                  )}
                  details={detailsUser(user)}
                  inactive={idsUnfilteredUsers.includes(user.id)}
                  enableNewCard
                  newCard={idsNewUsers.includes(user.id)}
                />
              </Grid>
            ))}
        </Grid>
        {users && nextPage < totalPages && (
          <Styled.ContainerShowMoreButton>
            <ShowMoreButton onClick={handleNextPage} />
          </Styled.ContainerShowMoreButton>
        )}
      </Styled.Container>
    </Office>
  );
};

export default Permission;
